import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import "./ModelViewer.scss";
import Loader from "components/Loader";

const ModelViewer = ({
  ar,
  arModes,
  cameraControls,
  toneMapping,
  poster,
  shadowIntensity,
  environmentImage,
  autoRotate,
  cameraPosition,
  config,
  disablePan,
  loading,
  setLoading,
  showHotspots,
  skyboxImage,
  exposure,
  resetCamera,
  onActiveHotspotChange,
  externalActiveHotspotId,
  onHotspotMark,
  selectedSkybox,
  scale,
}) => {
  const { src, hotspots = [], additionalAssets } = config;
  const modelViewerRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [activeHotspot, setActiveHotspot] = useState(
    externalActiveHotspotId || hotspots?.[0]?.slot || ""
  );
  const [prevShowHotspots, setPrevShowHotspots] = useState("");
  const [prevCameraPosition, setPrevCameraPosition] = useState(cameraPosition);
  const [prevSkybox, setPrevSkybox] = useState(null);
  const [userInteracted, setUserInteracted] = useState(false);
  const mouseDownPos = useRef({ x: 0, y: 0 });
  const autoMoveInterval = useRef(null);

  const annotationClicked = (event, hotspot) => {
    const modelViewer = modelViewerRef.current;
    if (modelViewer) {
      if (event) {
        const positionAndNormal = modelViewer.positionAndNormalFromPoint(
          event.clientX,
          event.clientY
        );
        const orbit = modelViewer.getCameraOrbit();
        if (positionAndNormal)
          console.log(
            "hotspot",
            hotspot.annotation,
            JSON.stringify({
              normal: positionAndNormal.normal.toString(),
              modelOrbit: orbit.toString(),
              modelPosition: positionAndNormal.position.toString(),
            })
          );
      }
      modelViewer.cameraTarget = cameraPosition.cameraTarget;
      modelViewer.fieldOfView = cameraPosition.fieldOfView;
      modelViewer.cameraOrbit = hotspot.orbit;
      setActiveHotspot(hotspot.slot);
      onActiveHotspotChange?.(hotspot.slot);
    } else {
      if (autoMoveInterval.current) {
        clearInterval(autoMoveInterval.current);
        autoMoveInterval.current = null;
      }
    }
  };

  const handleClick = async (event) => {
    const modelViewer = modelViewerRef.current;
    if (loading || !modelViewer.model || event.button !== 0) return;
    const dx = event.clientX - mouseDownPos.current.x;
    const dy = event.clientY - mouseDownPos.current.y;
    const dragDistance = Math.sqrt(dx * dx + dy * dy);
    if (dragDistance > 5) return; // Ignore if it's a drag, not a click

    setUserInteracted(true);
    if (autoMoveInterval.current) {
      clearInterval(autoMoveInterval.current);
      autoMoveInterval.current = null;
    }

    const positionAndNormal = modelViewer.positionAndNormalFromPoint(
      event.clientX,
      event.clientY
    );

    if (positionAndNormal && onHotspotMark) {
      const orbit = modelViewer.getCameraOrbit();
      onHotspotMark({
        position: positionAndNormal.position,
        normal: positionAndNormal.normal,
        cameraOrbit: orbit,
      });
    }

    const hit = await modelViewer.materialFromPointExtended?.(
      event.clientX,
      event.clientY
    );
    if (!hit?.material) {
      resetCamera();
    }
  };

  const onMouseDown = (event) => {
    if (event.button === 0) {
      mouseDownPos.current = { x: event.clientX, y: event.clientY };
    }
  };

  const startAutoMove = () => {
    if (!userInteracted && hotspots.length > 1) {
      let currentIndex = hotspots.findIndex((h) => h.slot === activeHotspot);
      autoMoveInterval.current = setInterval(() => {
        currentIndex = (currentIndex + 1) % hotspots.length;
        const nextHotspot = hotspots[currentIndex];
        annotationClicked(null, nextHotspot);
      }, 3000);
    }
  };

  useEffect(() => {
    const modelViewer = modelViewerRef.current;
    const onBeforeRender = () => {
      if (modelViewerRef.current) {
        const modelViewer = modelViewerRef.current;
        const scene =
          modelViewer[
            Object.getOwnPropertySymbols(modelViewer).find(
              (e) => e.description === "scene"
            )
          ];
        // Apply scale to a common parent so all assets scale together
        const groupToScaleInit =
          (scene && (scene.pivot || scene.target || scene.model)) || null;
        if (groupToScaleInit && scale && typeof scale.x === "number") {
          groupToScaleInit.scale.set(scale.x || 1, scale.y || 1, scale.z || 1);
        }
        const hairMesh =
          scene.getObjectByName("Shoulder_Hair_OBJ") ||
          scene.getObjectByName("dopplr_hair") ||
          scene.getObjectByName("dopplr_hair2");
        if (hairMesh) {
          const cloneHairMesh = hairMesh.clone();
          cloneHairMesh.name = `${hairMesh.name}_clone`;

          // Clone material
          const hairMaterial = hairMesh.material.clone();

          // Simulate MATERIAL_ALPHATEST
          hairMaterial.alphaTest = 0.5; // Typical threshold
          hairMaterial.transparent = false; // ALPHATEST: not alpha blended
          hairMaterial.depthWrite = true;

          // No specular and metallic (match Babylon settings)
          hairMaterial.roughness = 1; // Maximum roughness
          hairMaterial.metalness = 0; // No metallic

          // Set original hairMesh to same settings too
          hairMesh.material.roughness = 1;
          hairMesh.material.metalness = 0;
          cloneHairMesh.material = hairMaterial;
          cloneHairMesh.scale.y = 0.88 * cloneHairMesh.scale.y;
          scene.model.add(cloneHairMesh);
        }
      }
      setTimeout(() => {
        // setProgress("");
        setLoading(false);
      }, 10);
    };

    const handleLoading = () => {
      resetCamera();
      onBeforeRender();
    };
    const handleProgress = (event) => {
      setLoading(true);
      const { totalProgress } = event.detail;
      setProgress(Math.round(Number(totalProgress) * 100));
    };
    if (modelViewer && loading && progress === 100) {
      setLoading(false);
    }
    modelViewer.addEventListener("progress", handleProgress);
    modelViewer.addEventListener("load", handleLoading);
    return () => {
      modelViewer.removeEventListener("progress", handleProgress);
      modelViewer.removeEventListener("load", handleLoading);
    };
  }, [progress, setLoading, setProgress, loading, resetCamera]);

  // Update scale dynamically when prop changes and model is available
  useEffect(() => {
    const mv = modelViewerRef.current;
    if (!mv || !scale) return;
    const scene =
      mv[
        Object.getOwnPropertySymbols(mv).find((e) => e.description === "scene")
      ];
    const groupToScale =
      (scene && (scene.pivot || scene.target || scene.model)) || null;
    if (groupToScale && typeof scale.x === "number") {
      groupToScale.scale.set(scale.x || 1, scale.y || 1, scale.z || 1);
    }
  }, [scale]);

  useEffect(() => {
    if (externalActiveHotspotId !== undefined) {
      setActiveHotspot(externalActiveHotspotId);
    }
  }, [externalActiveHotspotId]);

  const animateFeatures = () => {
    if (showHotspots && hotspots.length > 0) {
      modelViewerRef.current.interactionPrompt = "none";
      setTimeout(() => {
        annotationClicked(
          null,
          hotspots.find((h) => h.slot === activeHotspot)
        );
        startAutoMove();
      }, 500);
    } else {
      if (autoMoveInterval.current) {
        clearInterval(autoMoveInterval.current);
        autoMoveInterval.current = null;
      }
      setUserInteracted(false);
    }
  };

  if (prevCameraPosition !== cameraPosition) {
    setPrevCameraPosition(cameraPosition);
    modelViewerRef.current.cameraTarget = cameraPosition.cameraTarget;
    modelViewerRef.current.fieldOfView = cameraPosition.fieldOfView;
    modelViewerRef.current.cameraOrbit = cameraPosition.cameraOrbit;
    modelViewerRef.current.interactionPrompt =
      showHotspots && hotspots.length > 0 ? "none" : "auto";
  }

  if (
    modelViewerRef.current &&
    prevShowHotspots !== showHotspots &&
    activeHotspot
  ) {
    setPrevShowHotspots(showHotspots);
    animateFeatures();
  }

  if (
    modelViewerRef.current &&
    selectedSkybox?.environment &&
    prevSkybox !== selectedSkybox
  ) {
    setPrevSkybox(selectedSkybox);
    modelViewerRef.current.environmentImage = selectedSkybox.environment ?? "";
    modelViewerRef.current.skyboxImage = selectedSkybox.skybox ?? "";
    modelViewerRef.current.skyboxHeight = "1.5m";
  }

  return (
    <>
      <model-viewer
        id="model-viewer"
        ref={modelViewerRef}
        interpolation-decay="100"
        style={{ width: "100%", height: "100%" }}
        src={src}
        {...(additionalAssets ? { "additional-models": additionalAssets } : {})}
        {...(ar ? { ar: "" } : {})}
        ar-modes={arModes}
        {...(cameraControls ? { "camera-controls": "" } : {})}
        tone-mapping={toneMapping}
        poster={poster}
        exposure={exposure}
        shadow-intensity={shadowIntensity}
        skybox-height="1.5m"
        {...(skyboxImage ? { "skybox-image": skyboxImage } : {})}
        {...(environmentImage ? { "environment-image": environmentImage } : {})}
        {...(autoRotate ? { "auto-rotate": "" } : {})}
        {...(disablePan ? { "disable-pan": "" } : {})}
        field-of-view={cameraPosition.fieldOfView}
        camera-target={cameraPosition.cameraTarget}
        camera-orbit={cameraPosition.cameraOrbit}
        min-camera-orbit={cameraPosition.minCameraOrbit}
        max-camera-orbit={cameraPosition.maxCameraOrbit}
        onClick={handleClick}
        onMouseDown={onMouseDown}
      >
        {showHotspots &&
          hotspots.map((hotspot, index) => (
            <button
              key={index}
              className={`Hotspot ${
                activeHotspot === hotspot.slot ? "active" : ""
              }`}
              slot={hotspot.slot}
              data-position={hotspot.position}
              data-normal={hotspot.normal}
              data-visibility-attribute="visible"
              onClick={(e) => showHotspots && annotationClicked(e, hotspot)}
            >
              {hotspot.slot === activeHotspot && (
                <div
                  className={`HotspotAnnotation ${
                    hotspot.orbit &&
                    parseFloat(hotspot.orbit.split(" ")[0]) > 1.2
                      ? "right"
                      : ""
                  }`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {hotspot.annotation}
                </div>
              )}
            </button>
          ))}
        <div className="progress-bar hide" slot="progress-bar">
          <div className="update-bar"></div>
        </div>
        {/* <button slot="ar-button" id="ar-button">
        {arButtonText}
      </button> */}
      </model-viewer>
      {loading && (
        <Loader className="ModelViewer__Loader" progress={progress} />
      )}
    </>
  );
};

ModelViewer.propTypes = {
  config: PropTypes.shape({
    src: PropTypes.string,
    hotspots: PropTypes.arrayOf(
      PropTypes.shape({
        slot: PropTypes.string,
        position: PropTypes.string,
        normal: PropTypes.string,
        annotation: PropTypes.string,
        orbit: PropTypes.string,
      })
    ),
  }),
  selectedProduct: PropTypes.any,
  ar: PropTypes.bool,
  arModes: PropTypes.string,
  cameraControls: PropTypes.bool,
  toneMapping: PropTypes.string,
  poster: PropTypes.string,
  shadowIntensity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  environmentImage: PropTypes.string,
  cameraOrbit: PropTypes.string,
  fieldOfView: PropTypes.string,
  autoRotate: PropTypes.bool,
  cameraTarget: PropTypes.string,
  minCameraOrbit: PropTypes.string,
  maxCameraOrbit: PropTypes.string,
  arButtonText: PropTypes.string,
  disablePan: PropTypes.bool,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  exposure: PropTypes.string,
  resetCamera: PropTypes.func,
  onActiveHotspotChange: PropTypes.func,
  activeHotspot: PropTypes.string,
  onHotspotMark: PropTypes.func,
  scale: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
    z: PropTypes.number,
  }),
};

ModelViewer.defaultProps = {
  src: "",
  ar: true,
  arModes: "webxr scene-viewer quick-look",
  cameraControls: true,
  toneMapping: "aces",
  poster: "",
  shadowIntensity: "1",
  // skyboxImage: "/environment/pillars_1k.hdr",
  // environmentImage: "/environment/pillars_1k.hdr",
  // cameraOrbit: "-30.04deg 90.41deg 2.8m",
  fieldOfView: "13deg",
  autoRotate: false,
  // cameraTarget: "0.01m 1.25m 0m",
  maxCameraOrbit: "auto 120deg 2.8m",
  hotspots: [],
  arButtonText: "View in your space",
  disablePan: false,
  loading: true,
  setLoading: () => {},
  resetCamera: () => {},
  exposure: 1,
  onActiveHotspotChange: null,
  activeHotspot: undefined,
  onHotspotMark: null,
  scale: { x: 1, y: 1, z: 1 },
};

export default ModelViewer;
